import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const PrivacyPolicy = () => {
  const accentColor = '#0d7377';
  const lastUpdated = 'April 24, 2026';

  return (
    <LegalLayout app="Clearcast" docType="Privacy Policy" icon={Shield} lastUpdated={lastUpdated}>
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Introduction
              </h2>
              <p className="mb-4 leading-relaxed">
                Feral Techlab ("we," "our," or "us") operates the Clearcast mobile application
                (the "App"). Clearcast is a weather-smart activity planner that scores each hour
                of the week against your outdoor activity preferences so you know exactly when
                conditions are best.
              </p>
              <p className="leading-relaxed">
                This Privacy Policy explains how we handle your information when you use our App.
                We are committed to protecting your privacy and being transparent about our practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Privacy Commitment
              </h2>
              <p className="mb-4 leading-relaxed">
                Clearcast is built with privacy as a core principle:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local-First Storage:</strong> All your activity profiles, preferences, and learning data are stored locally on your device.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No User Accounts:</strong> The App does not require registration, login, or any form of account creation.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Analytics:</strong> We do not collect usage statistics, behavioral data, or any form of telemetry.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Third-Party Tracking:</strong> We do not integrate any third-party analytics, tracking, or advertising SDKs.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>On-Device Learning:</strong> Health data analysis happens entirely on your device and never leaves it.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Information We Store
              </h2>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Activity Profiles
              </h3>
              <p className="mb-4 leading-relaxed">
                Clearcast stores the activity profiles you create or enable, which includes:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Activity name and icon</li>
                <li>Your weather preferences (temperature range, wind, precipitation, humidity, UV)</li>
                <li>Preferred times of day (morning, afternoon, evening, night)</li>
                <li>Scoring weights for each factor</li>
              </ul>
              <p className="leading-relaxed">
                <strong style={{ color: 'var(--text-primary)' }}>Important:</strong> All activity
                data is stored locally on your device using Apple's SwiftData framework and is
                never transmitted to any external server.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Location Data
              </h3>
              <p className="leading-relaxed">
                Clearcast uses CoreLocation with "When In Use" authorization at kilometer-level
                accuracy to determine your current location for fetching a weather forecast. Your
                location is used solely to request weather data through Apple's WeatherKit and is
                never transmitted to or stored on our servers. We also reverse-geocode your
                location via Apple's services to display a friendly place name (e.g. your city).
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Weather Data
              </h3>
              <p className="leading-relaxed">
                Clearcast uses Apple's WeatherKit framework to fetch hourly weather forecasts for
                the coming week. Requests go directly through Apple's services. We do not collect,
                store, or process any location or weather data on our servers.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Apple Health Data (Optional, Pro Feature)
              </h3>
              <p className="mb-4 leading-relaxed">
                If — and only if — you explicitly grant permission, Clearcast may read the
                following data from Apple Health to learn your preferences over time:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Recent outdoor workouts (type, start/end time, duration, distance, calories)</li>
                <li>Step counts during recommended activity windows</li>
                <li>Walking/running and cycling distance</li>
                <li>Active energy burned</li>
              </ul>
              <p className="leading-relaxed">
                <strong style={{ color: 'var(--text-primary)' }}>Health data never leaves your device.</strong>
                {' '}All analysis runs locally; the App produces preference insights (for example,
                "you run 80% of the time in the morning") that you can choose to apply to your
                profiles. You can revoke HealthKit access at any time from Settings → Privacy &
                Security → Health → Clearcast.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Notifications
              </h3>
              <p className="leading-relaxed">
                If you grant notification permission, Clearcast schedules a daily briefing at the
                time you choose and — for Pro subscribers who opt in — alerts 30 minutes before
                high-scoring activity windows. All notifications are scheduled on-device through
                iOS; we never see or send push notifications from our servers.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                User Preferences
              </h3>
              <p className="mb-4 leading-relaxed">
                The App stores your preferences locally, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Onboarding completion flag</li>
                <li>Briefing time (hour and minute)</li>
                <li>Temperature unit (°C / °F)</li>
                <li>Window Alerts toggle</li>
                <li>Dismissed preference insights (to avoid re-showing them)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Storage &amp; Security
              </h2>
              <p className="mb-4 leading-relaxed">
                Your data is protected by multiple layers of security:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local Storage:</strong> All data is stored in an on-device SwiftData store and UserDefaults, protected by iOS's built-in encryption.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Remote Servers:</strong> Activity data, health data, and preference insights are never uploaded anywhere.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>iOS Sandbox:</strong> The App operates within iOS's security sandbox, preventing other apps from accessing your data.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                What We Do NOT Collect
              </h2>
              <p className="mb-4 leading-relaxed">
                Clearcast does not collect, store, or transmit:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information</li>
                <li>Health or fitness data (health data is processed on-device only)</li>
                <li>Your real-time device location on our servers</li>
                <li>Contact information</li>
                <li>Photos or media files</li>
                <li>Browsing history</li>
                <li>Device identifiers for tracking purposes</li>
                <li>Usage analytics or behavioral data</li>
                <li>Advertising data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Subscriptions &amp; In-App Purchases
              </h2>
              <p className="mb-4 leading-relaxed">
                Clearcast offers an optional Pro subscription (monthly or yearly) that unlocks
                additional features such as unlimited activity profiles, the 7-day planner, Apple
                Health-based Insights, and Window Alerts. All purchases are processed entirely by
                the Apple App Store. We do not process, store, or have access to your payment
                information.
              </p>
              <p className="leading-relaxed">
                Entitlement is verified live through Apple's StoreKit framework on each app
                launch. We store only the set of purchased product identifiers in memory while
                the app is running; we do not persist any subscription or billing data to our
                servers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Third-Party Services
              </h2>
              <p className="leading-relaxed">
                Clearcast uses <strong style={{ color: 'var(--text-primary)' }}>Apple's native frameworks only</strong>:
                SwiftUI, SwiftData, WeatherKit, CoreLocation, HealthKit, UserNotifications, StoreKit,
                and Swift Charts. We do not integrate any third-party analytics tools, crash reporters,
                or advertising networks.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Rights &amp; Data Control
              </h2>
              <p className="mb-4 leading-relaxed">
                You have complete control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>View &amp; Edit:</strong> Review and customize every activity profile directly in the App.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Delete:</strong> Disable or remove activities at any time.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Revoke Permissions:</strong> Turn off Location, HealthKit, or Notifications at any time in iOS Settings; the App will continue to work with reduced functionality.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Uninstall:</strong> Removing the App deletes all locally stored data permanently.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                Clearcast is not directed at children under the age of 13. We do not knowingly
                collect personal information from children. If you believe a child has provided
                us with personal information, please contact us so we can take appropriate action.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on this page and updating the "Last
                updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices,
                please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:support@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    support@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/clearcast/support"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    Clearcast Support Page
                  </Link>
                </p>
              </div>
            </section>
          </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
